import { useCallback, useEffect, useState } from 'react'

/**
 * The browser's idea of where the user is, shared across every component that
 * wants it.
 *
 * Why a module-level cache rather than a plain `useEffect` per component:
 * `navigator.geolocation.getCurrentPosition` is asynchronous and, on a cold
 * permission grant, slow. A dialog that asks for the fix when it mounts spends
 * its first few keystrokes with no fix at all, which is exactly the window in
 * which the user types their search, so the first (and often only) query runs
 * unbiased and returns results from whatever region the provider's IP lookup
 * guessed. Resolving once per tab and handing the cached fix to every later
 * mount closes that window for every dialog after the first.
 */

export interface UserLocation {
	lat: number
	lng: number
	/**
	 * Radius of the fix in metres, straight from the Geolocation API.
	 *
	 * This is the field that tells a real fix from a useless one, and callers
	 * are expected to look at it. A wifi or GPS fix is tens of metres. An
	 * IP-derived fix is kilometres, and lands on the ISP's point of presence,
	 * which is how a search ends up ranked around a town the user is merely
	 * near. See `isPreciseFix`.
	 */
	accuracy: number
}

export type UserLocationStatus = 'idle' | 'locating' | 'ready' | 'unavailable'

const STORAGE_KEY = 'mdt.userLocation'

/**
 * Above this radius the fix is not describing where the user is, it is
 * describing their internet connection. 5km is generous: a wifi-triangulated
 * fix on a laptop is usually well under 200m, and an IP fix is rarely better
 * than a few kilometres.
 */
export const PRECISE_FIX_METRES = 5000

export const isPreciseFix = (loc: UserLocation | null): boolean =>
	!!loc && Number.isFinite(loc.accuracy) && loc.accuracy <= PRECISE_FIX_METRES

/**
 * `enableHighAccuracy: true` on purpose, and it is the setting that matters
 * most here.
 *
 * With it off, Chrome is free to answer from its IP-geolocation path alone,
 * which resolves to the ISP's point of presence: the right region, the wrong
 * town, and no way for the user to tell. With it on, the browser prefers wifi
 * scan data (and GPS where there is one), which is the difference between
 * "your street" and "a town twenty miles away".
 *
 * The usual argument against it is battery, and it does not apply: this runs
 * at most once per tab, not in a watch loop.
 *
 * `maximumAge` is short so a bad early fix does not get served for the rest of
 * the session, and `timeout` stops a request that would otherwise hang forever
 * from pinning the status at 'locating'.
 */
const GEO_OPTIONS: PositionOptions = {
	enableHighAccuracy: true,
	timeout: 15000,
	maximumAge: 60 * 1000,
}

/** A forced retry takes no cached answer at all. */
const FORCED_GEO_OPTIONS: PositionOptions = {
	...GEO_OPTIONS,
	maximumAge: 0,
}

let cached: UserLocation | null = null
let inFlight: Promise<UserLocation | null> | null = null
let resolvedOnce = false
/**
 * Whether this page load has asked the browser for a real fix yet.
 *
 * Separate from `cached` because a fix restored from `sessionStorage` must NOT
 * count as having asked. Conflating the two is what froze a stale fix in place
 * for a whole session: the stored value populated `cached`, `cached` short
 * circuited the request, and the "refreshed in the background" the code claimed
 * to do never happened.
 */
let requestedThisLoad = false

const subscribers = new Set<(value: UserLocation | null) => void>()

function readStored(): UserLocation | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY)
		if (!raw) return null
		const parsed = JSON.parse(raw)
		if (typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number') {
			return {
				lat: parsed.lat,
				lng: parsed.lng,
				// Older entries predate the field. Treat an unknown radius as
				// imprecise rather than assuming the best.
				accuracy: typeof parsed.accuracy === 'number' ? parsed.accuracy : Number.POSITIVE_INFINITY,
			}
		}
	} catch {
		// Private mode, blocked storage, or a malformed entry. No fix, no harm.
	}
	return null
}

function writeStored(value: UserLocation) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
	} catch {
		// Storage unavailable. The module-level cache still covers this tab.
	}
}

function publish(value: UserLocation | null) {
	cached = value
	resolvedOnce = true
	subscribers.forEach(fn => fn(value))
}

/**
 * Resolve the fix once per tab. Concurrent callers share the same promise, so
 * three dialogs mounting together produce one permission prompt, not three.
 */
export function requestUserLocation(force = false): Promise<UserLocation | null> {
	if (!force && inFlight) return inFlight
	// A fix this page load already fetched from the browser is good enough.
	// A fix merely restored from storage is not: it still needs one real ask.
	if (!force && cached && requestedThisLoad) return Promise.resolve(cached)
	// Already asked and were turned down. Asking again on every dialog mount
	// would re-prompt a user who has already said no.
	if (!force && resolvedOnce && requestedThisLoad && !cached) return Promise.resolve(null)

	if (typeof navigator === 'undefined' || !navigator.geolocation) {
		requestedThisLoad = true
		publish(null)
		return Promise.resolve(null)
	}

	requestedThisLoad = true
	inFlight = new Promise<UserLocation | null>(resolve => {
		navigator.geolocation.getCurrentPosition(
			pos => {
				const value = {
					lat: pos.coords.latitude,
					lng: pos.coords.longitude,
					accuracy: pos.coords.accuracy,
				}
				writeStored(value)
				publish(value)
				resolve(value)
			},
			() => {
				// Denied, timed out, or unavailable. Keep a stored fix if we
				// have one rather than throwing away the only signal we had.
				publish(cached)
				resolve(cached)
			},
			force ? FORCED_GEO_OPTIONS : GEO_OPTIONS,
		)
	}).finally(() => {
		inFlight = null
	})

	return inFlight
}

export interface UseUserLocationResult {
	location: UserLocation | null
	status: UserLocationStatus
	/** True only when the radius is small enough to rank search results by. */
	isPrecise: boolean
	/** Ask again, forcing a fresh fix. Backs the "Use my location" action. */
	refresh: () => void
}

export default function useUserLocation(enabled = true): UseUserLocationResult {
	const [location, setLocation] = useState<UserLocation | null>(() => cached || readStored())
	const [status, setStatus] = useState<UserLocationStatus>(() =>
		cached || readStored() ? 'ready' : resolvedOnce ? 'unavailable' : 'idle',
	)

	useEffect(() => {
		if (!enabled) return
		let alive = true

		const subscriber = (value: UserLocation | null) => {
			if (!alive) return
			setLocation(value)
			setStatus(value ? 'ready' : 'unavailable')
		}
		subscribers.add(subscriber)

		// A stored fix is served immediately so the dropdown is never unbiased
		// while we wait, and a real request still goes out underneath it.
		const stored = readStored()
		if (stored && !cached) {
			cached = stored
			setLocation(stored)
			setStatus('ready')
		}
		if (!cached) setStatus(requestedThisLoad && resolvedOnce ? 'unavailable' : 'locating')

		void requestUserLocation()

		return () => {
			alive = false
			subscribers.delete(subscriber)
		}
	}, [enabled])

	const refresh = useCallback(() => {
		setStatus('locating')
		void requestUserLocation(true)
	}, [])

	return { location, status, isPrecise: isPreciseFix(location), refresh }
}
